import { useState, useRef } from "react"
import * as Sentry from "@sentry/react"
import { Button } from "@aix/ui"

import type { DiagramSpec, RecentDiagram } from "../types"
import { validateSpec } from "../validator"
import { isCompactSpec, expandSpec } from "../compact"

const STORAGE_KEY = "diagrammer:recent-files"

function getRecents(): RecentDiagram[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")
  } catch {
    return []
  }
}

function saveRecent(entry: RecentDiagram) {
  const recents = getRecents().filter((r) => r.fileName !== entry.fileName)
  recents.unshift(entry)
  localStorage.setItem(STORAGE_KEY, JSON.stringify(recents.slice(0, 10)))
}

interface Props {
  onLoad: (spec: DiagramSpec, fileName: string) => void
  currentName: string
}

export function FilePicker({ onLoad, currentName }: Props) {
  const [showRecents, setShowRecents] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const recents = getRecents()

  const handleFile = (file: File) => {
    setError(null)
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string)
        const resolved = isCompactSpec(parsed) ? expandSpec(parsed) : parsed
        const errors = validateSpec(resolved).filter((e) => e.severity === "error")
        if (errors.length > 0) {
          setError(`Validation failed:\n${errors.map((e) => `  ${e.path}: ${e.message}`).join("\n")}`)
          return
        }
        const spec = resolved as DiagramSpec
        saveRecent({
          name: spec.name,
          fileName: file.name,
          loadedAt: new Date().toISOString(),
          spec,
        })
        onLoad(spec, file.name)
        setShowRecents(false)
      } catch (e) {
        Sentry.captureException(e, { tags: { feature: "diagrammer-file-load" } })
        setError(`Invalid JSON: ${(e as Error).message}`)
        return
      }
    }
    reader.readAsText(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) handleFile(file)
        }}
      />

      <div className="flex gap-1.5">
        <Button
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="flex-1"
        >
          Load JSON
        </Button>

        {recents.length > 0 && (
          <Button
            variant={showRecents ? "default" : "outline"}
            size="sm"
            onClick={() => setShowRecents(!showRecents)}
            aria-label="Show recent files"
          >
            ↓
          </Button>
        )}
      </div>

      {error && (
        <div className="mt-1.5 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1.5 text-[11px] text-destructive whitespace-pre-wrap leading-relaxed">
          {error}
        </div>
      )}

      {showRecents && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 max-h-64 overflow-y-auto rounded-lg border border-border bg-popover shadow-xl">
          <div className="px-2.5 py-2 text-[10px] font-bold tracking-[0.1em] uppercase text-muted-foreground border-b border-border">
            Recent
          </div>
          {recents.map((r) => (
            <button
              key={r.fileName + r.loadedAt}
              onClick={() => {
                onLoad(r.spec, r.fileName)
                setShowRecents(false)
              }}
              className={`block w-full text-left px-2.5 py-2 border-b border-border text-foreground transition-colors hover:bg-accent hover:text-accent-foreground ${
                r.name === currentName ? "bg-accent/40" : ""
              }`}
            >
              <div className="text-xs font-semibold">{r.name}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {r.fileName} · {new Date(r.loadedAt).toLocaleDateString()}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
